import { Link } from "react-router";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { useCustomizationLocked } from "@/lib/use-customization-locked";
import { ClientPriceEditor } from "@/components/catalogo/ClientPriceEditor";

type Cadastro = { id: string; linkedOrganizationId: string | null; isPartner: boolean; priceTableId: string | null };
const cartao = "rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card";

/**
 * Os preços deste cliente: a parceria (que libera preço personalizado), a tabela nomeada ou a
 * configuração individual (percentual e preços fixos). Preço personalizado é do plano pago — sem ele
 * tudo aparece travado, como no Catálogo › Preços.
 */
export function PrecosDoCadastro({ cadastro: c }: { cadastro: Cadastro }) {
  const utils = trpc.useUtils();
  const ehDono = useAuth().user?.activeOrganization?.role === "OWNER";
  const semPersonalizacao = useCustomizationLocked();
  const podeEditarPreco = usePermissao("prices.manage");
  const locked = semPersonalizacao || !podeEditarPreco;
  const tabelas = trpc.labPricing.priceTables.list.useQuery().data ?? [];
  const invalidar = () => {
    utils.partner.getById.invalidate({ id: c.id });
    utils.partner.list.invalidate();
    utils.labPricing.priceTables.list.invalidate();
    utils.labPricing.clientPricing.listPartners.invalidate();
  };
  const setIsPartner = trpc.partner.setIsPartner.useMutation({ onSuccess: () => (toast.success("Parceria atualizada."), invalidar()), onError: (e) => toast.error(e.message) });
  const setPriceTable = trpc.partner.setPriceTable.useMutation({ onSuccess: () => (toast.success("Tabela de preço atualizada."), invalidar()), onError: (e) => toast.error(e.message) });
  const tabela = tabelas.find((t) => t.id === c.priceTableId);

  return (
    <div className="space-y-4">
      <section className={cartao}>
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Parceria</h3>
        <label className="mt-3 flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={c.isPartner}
            disabled={!ehDono || setIsPartner.isPending}
            onChange={(e) => setIsPartner.mutate({ id: c.id, isPartner: e.target.checked })}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
          />
          <span>
            <span className="block text-sm font-medium text-zinc-900 dark:text-foreground">Clínica parceira</span>
            <span className="block text-[12px] text-zinc-500 dark:text-muted-foreground">
              Libera preço personalizado pra este cliente: percentual, preços fixos ou uma tabela nomeada.
              {!ehDono && " Só o proprietário muda a parceria."}
            </span>
          </span>
        </label>
        {c.isPartner && (
          <label className="mt-4 block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">Tabela de preço</span>
            <select
              value={c.priceTableId ?? ""}
              disabled={locked || setPriceTable.isPending}
              onChange={(e) => setPriceTable.mutate({ id: c.id, priceTableId: e.target.value || null })}
              className="h-9 w-full max-w-md rounded-md border border-zinc-200 bg-white px-2.5 text-[13px] text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
            >
              <option value="">Configuração individual (percentual e preços fixos próprios)</option>
              {tabelas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </section>

      {!c.isPartner ? (
        <p className={`${cartao} text-sm text-zinc-500 dark:text-muted-foreground`}>
          Sem parceria, este cliente paga a tabela base do laboratório (Catálogo › Preços).
        </p>
      ) : tabela ? (
        <p className={`${cartao} text-sm text-zinc-600 dark:text-muted-foreground`}>
          Segue a tabela <b className="text-zinc-900 dark:text-foreground">{tabela.name}</b> ({tabela.adjustmentPercent > 0 ? "+" : ""}
          {tabela.adjustmentPercent}% sobre a base). Os preços dela se editam em{" "}
          <Link to="/lab/catalogo/gerenciar#precos" className="font-semibold text-brand-700 hover:underline dark:text-primary">
            Catálogo › Preços
          </Link>
          .
        </p>
      ) : (
        <section className={cartao}>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Preços deste cliente</h3>
          <ClientPriceEditor clientOrganizationId={c.linkedOrganizationId!} locked={locked} />
        </section>
      )}
    </div>
  );
}
